import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from '../components/Icon';
import { useDocumentHead } from '../lib/seo';

const LAST_UPDATED = '16 May 2026';

export default function TermsPage() {
  const navigate = useNavigate();

  useDocumentHead({
    title: 'Terms of service · ParkProof',
    description:
      'The rules for using ParkProof. ParkProof is provided as-is — always verify clearance heights yourself before entering a parking garage.',
    canonicalPath: '/terms',
  });

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <article className="p-s5 flex flex-col gap-s5 legal-prose">
        <header className="flex flex-col gap-s2">
          <div className="caption text-ink3 font-mono">LEGAL</div>
          <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
            Terms of <span className="font-serif-italic">service</span>
          </h1>
          <p className="font-mono text-[11px] text-ink3 tracking-wide">
            Last updated {LAST_UPDATED}
          </p>
        </header>

        <Section title="1. About these terms">
          <p>
            These terms form a binding agreement between you and the operator of
            ParkProof:
          </p>
          <p className="font-medium text-ink1">
            Florian Le Rouzic (Einzelunternehmen)
            <br />
            Stauffacherstrasse 179, 8004 Zürich, Switzerland
            <br />
            UID: CHE-{/* TODO: fill in UID-CHE number once registered */}TBD
            <br />
            Contact: <a className="underline" href="mailto:contact@parkproof.app">contact@parkproof.app</a>
          </p>
          <p>
            "ParkProof", "we", "us" and "our" refer to the operator above. "You"
            means the person using the service. By creating an account or using
            ParkProof in any way, you accept these terms.
          </p>
        </Section>

        <Section title="2. What ParkProof is">
          <p>
            ParkProof is a free, community-supported directory of public parking
            garages with crowd-sourced clearance-height information. We help
            drivers of tall cars, SUVs, vans, and cars with roof boxes or roof
            racks figure out which garages are likely to fit their vehicle.
          </p>
          <p>
            <strong>
              ParkProof is informational only. Heights shown in the app are
              estimates and user-submitted reports — not measurements we make
              ourselves and not legal advice or guidance from the garage operator.
            </strong>
          </p>
        </Section>

        <Section title="3. You are responsible for verifying clearance">
          <p>
            <strong>
              Always visually verify the posted clearance height at the entrance of
              any parking garage before driving in.
            </strong>{' '}
            Garage operators can change heights, install new equipment, or post
            different limits at different entrances. Roof racks, roof boxes,
            antennas, snow on the roof, suspension setup, and tyre pressure all
            change your effective vehicle height.
          </p>
          <p>
            ParkProof cannot and does not guarantee that any specific parking
            facility will fit your vehicle. The final decision to enter a garage
            is yours.
          </p>
        </Section>

        <Section title="4. Your account">
          <p>
            You can browse most of ParkProof without an account. To save cars to
            your garage or submit height verifications, you need to sign in with
            an email address, a Google account, or an Apple ID.
          </p>
          <p>
            You are responsible for keeping your sign-in credentials secure and
            for everything done under your account. You must be at least 13 years
            old to create an account.
          </p>
        </Section>

        <Section title="5. Content you submit">
          <p>
            When you submit a height verification, note, photo, or other content
            ("User Content"), you keep ownership of it, but you grant ParkProof a
            worldwide, non-exclusive, royalty-free licence to host, store,
            reproduce, display, and distribute that content within the service —
            including showing your submissions publicly so other drivers benefit.
          </p>
          <p>You agree that your User Content:</p>
          <ul>
            <li>is accurate to the best of your knowledge;</li>
            <li>is yours to submit, or you have permission to submit it;</li>
            <li>
              does not contain anything illegal, hateful, defamatory, infringing,
              or invasive of someone else's privacy.
            </li>
          </ul>
          <p>
            We may remove or edit User Content that we believe violates these
            terms or harms other users, without prior notice.
          </p>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to:</p>
          <ul>
            <li>scrape, copy, or resell ParkProof data in bulk;</li>
            <li>
              submit deliberately false height information or otherwise tamper
              with the integrity of the parking data;
            </li>
            <li>
              use the service to harass other users or to break any law;
            </li>
            <li>
              attempt to reverse-engineer, attack, or disrupt the service or its
              infrastructure;
            </li>
            <li>
              create accounts in bulk, impersonate other people, or share
              credentials.
            </li>
          </ul>
        </Section>

        <Section title="7. Free service, no warranty">
          <p>
            ParkProof is provided <strong>"as is" and "as available"</strong> at no
            cost. To the maximum extent permitted by law, we disclaim all
            warranties — express or implied — including warranties of accuracy,
            fitness for a particular purpose, non-infringement, and
            uninterrupted availability.
          </p>
        </Section>

        <Section title="8. Limitation of liability">
          <p>
            To the maximum extent permitted by Swiss law, ParkProof (Florian Le
            Rouzic and any contributors) is not liable for any indirect,
            incidental, consequential, special, or punitive damages, including
            but not limited to vehicle damage, property damage, lost time, lost
            data, or lost profits arising from your use of — or inability to use
            — ParkProof, even if we have been advised of the possibility of such
            damages.
          </p>
          <p>
            Nothing in these terms excludes or limits liability for gross
            negligence, intentional wrongdoing, personal injury, or any other
            liability that cannot be excluded under mandatory Swiss law.
          </p>
        </Section>

        <Section title="9. Termination">
          <p>
            You may close your account at any time. We may suspend or terminate
            access if you breach these terms, if your account is being used to
            harm others, or if we have to stop offering the service. Sections
            that by their nature should survive (such as the limitation of
            liability) will continue to apply after termination.
          </p>
        </Section>

        <Section title="10. Changes to the service and these terms">
          <p>
            ParkProof evolves over time. We may add, change, or remove features
            at our discretion. We may also update these terms; we will update the
            "Last updated" date above and, for material changes, give notice in
            the app or by email before the changes take effect. Continued use of
            the service after the changes take effect means you accept them.
          </p>
        </Section>

        <Section title="11. Governing law and jurisdiction">
          <p>
            These terms are governed by the substantive laws of Switzerland,
            excluding its conflict-of-laws rules and the UN Convention on
            Contracts for the International Sale of Goods (CISG).
          </p>
          <p>
            The exclusive place of jurisdiction for any dispute arising out of or
            in connection with these terms is Zürich, Switzerland — subject to
            any mandatory consumer-protection rules that may grant you the right
            to sue at your place of residence.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions about these terms?{' '}
            <a className="underline" href="mailto:contact@parkproof.app">
              contact@parkproof.app
            </a>
          </p>
        </Section>
      </article>
    </div>
  );
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-s2">
    <h2 className="font-serif text-[20px] leading-[24px] text-ink1">{title}</h2>
    <div className="font-sans text-[15px] leading-[24px] text-ink2 flex flex-col gap-s3">
      {children}
    </div>
  </section>
);
